<template>
    <div class="page-wrapper">
        <div class="register-container">
            <div class="background-decoration">
                <div class="decoration-circle circle-1"></div>
                <div class="decoration-circle circle-2"></div>
                <div class="decoration-circle circle-3"></div>
            </div>

            <div class="header-section">
                <div class="back-button" @click="goToLogin">
                    <el-icon class="el-icon" size="24">
                        <ArrowLeft />
                    </el-icon>
                </div>

                <div class="header-content">
                    <div class="welcome-section">
                        <text class="welcome-title">{{ t('register.title') }}</text>
                        <text class="welcome-subtitle">{{ t('register.subtitle') }}</text>
                    </div>
                </div>
            </div>

            <div class="register-form-section">
                <div class="form-card">
                    <div class="form-header">
                        <text class="form-title">{{ t('register.title') }}</text>
                        <text class="form-subtitle">{{ t('register.subtitle') }}</text>
                    </div>


                    <div class="form-content">
                        <div class="input-group">
                            <div class="input-label">
                                <text class="label-text">{{ t('register.username') }}</text>
                            </div>
                            <div class="input-container">
                                <div class="input-icon">
                                    <text class="icon">👤</text>
                                </div>
                                <input type="text" v-model="formData.username" :placeholder="t('register.usernamePlaceholder')" class="form-input">
                            </div>
                        </div>
                        <div class="input-group">
                            <div class="input-label">
                                <text class="label-text">{{ t('register.phone') }}</text>
                            </div>
                            <div class="input-container">
                                <div class="input-icon">
                                    <text class="icon">📱</text>
                                </div>
                                <input type="text" v-model="formData.phone" :placeholder="t('register.phonePlaceholder')" class="form-input">
                            </div>
                        </div>
                        <div class="input-group">
                            <div class="input-label">
                                <text class="label-text">{{ t('register.loginPassword') }}</text>
                            </div>
                            <div class="input-container">
                                <div class="input-icon">
                                    <text class="icon">🔒</text>
                                </div>
                                <input :type="showLoginPassword ? 'text' : 'password'" v-model="formData.loginPassword" :placeholder="t('register.loginPasswordPlaceholder')" class="form-input">
                                <div class="input-action" @click="showLoginPassword = !showLoginPassword">
                                    <text class="action-text icon-text">{{ showLoginPassword ? t('login.hidePassword') : t('login.showPassword') }}</text>
                                </div>
                            </div>
                        </div>
                        <div class="input-group">
                            <div class="input-label">
                                <text class="label-text">{{ t('register.transactionPassword') }}</text>
                            </div>
                            <div class="input-container">
                                <div class="input-icon">
                                    <text class="icon">🔐</text>
                                </div>
                                <input :type="showTransactionPassword ? 'text' : 'password'" v-model="formData.transactionPassword" :placeholder="t('register.transactionPasswordPlaceholder')" class="form-input">
                                <div class="input-action" @click="showTransactionPassword = !showTransactionPassword">
                                    <text class="action-text icon-text">{{ showTransactionPassword ? t('login.hidePassword') : t('login.showPassword') }}</text>
                                </div>
                            </div>
                        </div>
                        <div class="input-group">
                            <div class="input-label">
                                <text class="label-text">{{ t('register.invitationCode') }}</text>
                                <text class="label-required">*</text>
                            </div>
                            <div class="input-container">
                                <div class="input-icon">
                                    <text class="icon">🎁</text>
                                </div>
                                <input type="text" v-model="formData.inviteCode" :placeholder="t('register.invitationCodePlaceholder')" class="form-input">
                            </div>
                        </div>
                        <div class="button-group">
                            <div class="primary-button" @click="handleRegister" :class="{ 'is-submitting': isSubmitting }">
                                <div class="button-content">
                                    <text class="button-text">{{ isSubmitting ? t('common.submitting') : t('register.registerButton') }}</text>
                                    <div class="button-icon">{{ isSubmitting ? '' : '✓' }}</div>
                                </div>
                            </div>
                        </div>
                        <div class="button-group">
                            <div class="secondary-button" @click="goToLogin">
                                <div class="button-content">
                                    <text class="button-text">{{ t('register.hasAccount') }}</text>
                                    <div class="button-icon">→</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ElMessage } from 'element-plus'
import { register } from '@/api'
import { TOKEN_KEY } from '@/libs/util'
import '@/assets/css/register.css'

const router = useRouter()
const route = useRoute()
const { t } = useI18n()

const goToLogin = () => {
  router.push('/login')
}

const showLoginPassword = ref(false)
const showTransactionPassword = ref(false)
const isSubmitting = ref(false)

const formData = ref({
  username: '',
  phone: '',
  loginPassword: '',
  transactionPassword: '',
  inviteCode: ''
})

const validatePassword = (password) => {
  if (password.length < 6) {
    return t('changePasswordPage.errorPasswordLength')
  }
  const hasLetter = /[a-zA-Z]/.test(password)
  const hasNumber = /[0-9]/.test(password)
  if (!hasLetter || !hasNumber) {
    return t('changePasswordPage.errorPasswordFormat')
  }
  return null
}

const handleRegister = async () => {
  if (isSubmitting.value) {
    return
  }

  if (!formData.value.username) {
    ElMessage.warning(t('register.errorUsername'))
    return
  }

  if (!formData.value.phone) {
    ElMessage.warning(t('register.errorPhone'))
    return
  }

  if (!formData.value.loginPassword) {
    ElMessage.warning(t('register.errorLoginPassword'))
    return
  }

  const loginPasswordError = validatePassword(formData.value.loginPassword)
  if (loginPasswordError) {
    ElMessage.warning(loginPasswordError)
    return
  }

  if (!formData.value.transactionPassword) {
    ElMessage.warning(t('register.errorTransactionPassword'))
    return
  }

  if (!formData.value.inviteCode) {
    ElMessage.warning(t('register.errorInviteCode'))
    return
  }

  isSubmitting.value = true

  try {
    const response = await register({
      tel: formData.value.phone,
      user_name: formData.value.username,
      pwd: formData.value.loginPassword,
      security_password: formData.value.transactionPassword,
      invite_code: formData.value.inviteCode
    })

    if (response.data.code === 200) {
      localStorage.setItem(TOKEN_KEY, response.data.data.token)
      ElMessage.success(t('register.registerSuccess'))
      setTimeout(() => {
        window.location.href = '/'
      }, 2000)
    } else {
      ElMessage.error(response.data.msg || t('common.submitFailed'))
      isSubmitting.value = false
    }
  } catch (error) {
    console.error('注册失败:', error)
    ElMessage.error(t('common.submitFailed'))
    isSubmitting.value = false
  }
}

onMounted(() => {
  const code = route.query.inviteCode
  if (code) {
    formData.value.inviteCode = code
  }
})
</script>

<style scoped>
.page-wrapper {
    scrollbar-width: none;
    -ms-overflow-style: none;
}

.page-wrapper::-webkit-scrollbar {
    display: none;
}

.header-section{
    padding: 20px 20px 15px;
    position: relative;
    z-index: 10
}

.header-content{
    margin-top: 40px
}

@media (max-width: 375px) {
    .header-section{
        padding:15px 15px 10px
    }

    .welcome-title{
        font-size: 28px
    }

    .welcome-subtitle{
        font-size: 13px
    }

    .register-form-section{
        padding: 0 15px 15px
    }

    .form-card{
        padding: 20px
    }

    .form-header{
        margin-bottom: 16px
    }

    .form-header .form-title{
        font-size: 20px
    }

    .input-group{
        margin-bottom: 14px
    }

    .form-input{
        height: 40px;
        font-size: 13px
    }

    .primary-button[data-v-3a3d1703],.secondary-button{
        height: 42px
    }

    .primary-button .button-text[data-v-3a3d1703],.secondary-button .button-text{
        font-size: 14px
    }
}
</style>


